// ═══════════════════════════════════════════════════════════════
// job-assets — find and delete every Storage file a job owns.
//
// Deleting a job or a customer used to remove Firestore documents
// only. Every job photo and every inspection photo stayed in Storage
// forever: unreachable from the app, invisible in the UI, and billed
// to the account owner every month. There was no way to find them
// again, let alone reclaim them.
//
// A digital inspection produces a photo per finding, so this is not a
// rounding error at any real volume.
// ═══════════════════════════════════════════════════════════════
import { getDoc, doc, deleteDoc } from 'firebase/firestore';
import { inspectionsCol } from '../database.js';
import { removeFiles } from './storage.js';
import { normalizeInspection } from './inspection-helpers.js';

// Storage paths on the job document itself (the Photos panel).
function jobPhotoPaths(job) {
  return (Array.isArray(job?.photos) ? job.photos : [])
    .map((p) => p?.path)
    .filter(Boolean);
}

// Storage paths inside an inspection: per-item photos, the tire
// grid's photos, and anything attached to a free-form recommendation.
export function inspectionPhotoPaths(inspection) {
  const insp = normalizeInspection(inspection);
  const paths = [...(Array.isArray(insp?.archivedPhotoPaths) ? insp.archivedPhotoPaths : [])];
  const collect = (photos) => {
    (Array.isArray(photos) ? photos : []).forEach((p) => {
      if (p?.path) paths.push(p.path);
    });
  };
  (insp?.sections || []).forEach((section) => {
    if (section.kind === 'tires') collect(section.tires?.photos);
    (section.items || []).forEach((item) => collect(item.photos));
  });
  (insp?.extraItems || []).forEach((item) => collect(item.photos));
  return paths;
}

// Remove a job's inspection document and every file either it or the
// job points at. Best-effort throughout: never let cleanup failure
// block the delete it accompanies.
export async function purgeJobAssets(jobId, job) {
  const paths = [...jobPhotoPaths(job)];
  try {
    const snap = await getDoc(doc(inspectionsCol(), jobId));
    if (snap.exists()) {
      paths.push(...inspectionPhotoPaths(snap.data()));
      await deleteDoc(doc(inspectionsCol(), jobId));
    }
  } catch (e) {
    console.warn(`[job-assets] could not read/remove inspection for job ${jobId}:`, e);
  }
  try {
    return await removeFiles(paths);
  } catch (e) {
    console.warn('[job-assets] file cleanup failed:', e);
    return 0;
  }
}

// Same, for every job belonging to a customer being deleted. Run in
// parallel — a customer with years of history shouldn't take a minute
// to delete.
export async function purgeJobsAssets(jobs) {
  const list = Array.isArray(jobs) ? jobs : [];
  const counts = await Promise.all(
    list.map((j) => purgeJobAssets(j.id, j).catch(() => 0))
  );
  return counts.reduce((a, b) => a + b, 0);
}
